import { createContext, useContext, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { useAuthStore } from "@/stores/auth-store";
import { AppShell } from "@/components/layout/AppShell";
import { PublicShareShell } from "@/components/layout/PublicShareShell";
import { RequireFeature } from "@/auth/require-feature";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { PublicShareAvailability } from "./PublicShareAvailability";
import { SharePage } from "./SharePage";
import { ShareItemDetailPage } from "./ShareItemDetailPage";
import { PublicSharePage } from "./PublicSharePage";
import { PublicShareItemPage } from "./PublicShareItemPage";
import { OpenListingsPage } from "./applications/OpenListingsPage";
import { OpenListingPage } from "./applications/OpenListingPage";
import { PublicOpenListingsPage } from "./applications/PublicOpenListingsPage";
import { PublicOpenListingPage } from "./applications/PublicOpenListingPage";

// One URL for both access modes (plan 7; same rule as the public Share preparation, spark#1179): a signed-in visitor
// gets the existing pages behind their existing feature gates, an anonymous one the public pages, fed only by the
// public schema, in the same frame. Login is offered, never required.
//
// The mode is settled once per page visit (location), not on every change of the session: a session that ends on the
// page keeps the AppShell, its SessionExpiredNotice and whatever the page holds (an open form, the confirmation token),
// and a login in another tab does not swap the public page away. The next navigation takes the session as it is then.
// Only a different signed-in person resets the private pages, so nothing typed for one account is sent as another.

interface ShareVisit {
  key: string;
  mode: "authenticated" | "anonymous";
  subject: string | null;
}

const ShareVisitContext = createContext<ShareVisit | null>(null);

function nextVisit(visit: ShareVisit | null, key: string, status: string, subject: string | null): ShareVisit | null {
  if (status === "loading") return visit;
  const otherPerson = visit?.mode === "authenticated" && subject !== null && subject !== visit.subject;
  if (visit && visit.key === key && !otherPerson) return visit;
  const authenticated = status === "authenticated";
  return { key, mode: authenticated ? "authenticated" : "anonymous", subject: authenticated ? subject : null };
}

export function ShareAccessShell() {
  const status = useAuthStore((state) => state.status);
  const subject = useAuthStore((state) => state.profile?.sub ?? null);
  const { key } = useLocation();
  const [visit, setVisit] = useState<ShareVisit | null>(null);
  const current = nextVisit(visit, key, status, subject);
  if (current !== visit) setVisit(current);
  if (!current) return null;
  return (
    <ShareVisitContext.Provider value={current}>
      {current.mode === "authenticated" ? <AppShell /> : <PublicShareAvailability><PublicShareShell /></PublicShareAvailability>}
    </ShareVisitContext.Provider>
  );
}

function useShareVisit(): ShareVisit {
  const visit = useContext(ShareVisitContext);
  if (!visit) throw new Error("Share access pages render inside ShareAccessShell");
  return visit;
}

/** Signed in: SHARE and the applicant pages' share.applications, exactly as before; anonymous: nothing to check. */
export function OpenListingsAccessGate() {
  const visit = useShareVisit();
  // Keyed by the person: another account starts the private pages afresh.
  return visit.mode === "authenticated"
    ? <RequireFeature key={visit.subject ?? ""} featureKey={FEATURE_CATALOG.share.key} />
    : <Outlet />;
}

export function ApplicantPagesAccessGate() {
  return useShareVisit().mode === "authenticated"
    ? <RequireFeature featureKey={FEATURE_CATALOG.shareApplications.key} />
    : <Outlet />;
}

export function OpenListingsAccessPage() {
  return useShareVisit().mode === "authenticated" ? <OpenListingsPage /> : <PublicOpenListingsPage />;
}

export function OpenListingAccessPage() {
  return useShareVisit().mode === "authenticated" ? <OpenListingPage /> : <PublicOpenListingPage />;
}

export function ShareAccessPage() {
  return useShareVisit().mode === "authenticated" ? <SharePage /> : <PublicSharePage />;
}

export function ShareAccessItemPage() {
  return useShareVisit().mode === "authenticated" ? <ShareItemDetailPage /> : <PublicShareItemPage />;
}
